import { useRef, useState, useEffect } from "react";
import {
  FiCalendar,
  FiChevronDown,
  FiX,
  FiSearch,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import { FaSpinner, FaPills } from "react-icons/fa";
import { message } from "antd";
import dayjs from "dayjs";
import ConsultationService from "../../../../Services/ConsultationService";
import IPDOrderService from "../../../../Services/IPDOrderService";
import DrugServices from "../../../../Services/DrugServices";

const ORDER_TYPES = ["MEDICATION", "RADIOLOGY", "PATHOLOGY"];
const PRIORITIES = ["High", "Medium", "Low"];
const MAX_CHARS = 250;

function formatDateTimePayload(dateStr) {
  if (!dateStr) return dayjs().format("YYYY-MM-DD HH:mm:ss");
  if (dateStr.length === 10) return `${dateStr} ${dayjs().format("HH:mm:ss")}`;
  return dateStr;
}

export default function AddOrderModal({
  onClose,
  onSuccess,
  patientDetails,
  patientId,
  admissionId,
  admission,
}) {
  const loginObj =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const userData = loginObj ? JSON.parse(loginObj) : null;
  const activePatient = JSON.parse(sessionStorage.getItem("patient") || "null");

  const effectivePatientId =
    patientId ||
    patientDetails?.patientId ||
    activePatient?.patientId ||
    admission?.patientId ||
    "";
  const effectiveAdmissionId =
    admissionId ||
    patientDetails?.admissionId ||
    activePatient?.admissionId ||
    admission?.admissionId ||
    admission?.id ||
    "";
  const effectiveAccountId =
    userData?.user?.account_Id ||
    patientDetails?.accountId ||
    activePatient?.accountId ||
    admission?.accountId ||
    "";
  const effectiveClinicId =
    userData?.user?.clinic_Id ||
    patientDetails?.clinicId ||
    activePatient?.clinicId ||
    admission?.clinicId ||
    "";
  const currentUserId = userData?.user?.userId || activePatient?.userId || "";

  const [formData, setFormData] = useState({
    orderType: "",
    orderDate: dayjs().format("YYYY-MM-DD"),
    priority: "Medium",
    clinicalNotes: "",
    instructions: "",
  });

  // Non-medication test search state
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);

  // Medication search & list state
  const [medSearchValue, setMedSearchValue] = useState("");
  const [medSearchResults, setMedSearchResults] = useState([]);
  const [selectedMedItem, setSelectedMedItem] = useState(null);
  const [isMedSearching, setIsMedSearching] = useState(false);
  const [medQuantity, setMedQuantity] = useState(1);
  const [medInstructions, setMedInstructions] = useState("");
  const [medicationsList, setMedicationsList] = useState([]);
  const medSearchRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults([]);
      }
      if (medSearchRef.current && !medSearchRef.current.contains(e.target)) {
        setMedSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "orderType") {
      setSelectedOrder(null);
      setSearchValue("");
      setSearchResults([]);
      setSelectedMedItem(null);
      setMedSearchValue("");
      setMedSearchResults([]);
      setMedQuantity(1);
      setMedInstructions("");
    }
  };

  const departmentCode =
    admission?.departmentId ||
    admission?.departmentCode ||
    patientDetails?.departmentId ||
    patientDetails?.departmentCode ||
    (formData.orderType === "MEDICATION" ? "DXDP025" : formData.orderType);

  // Non-medication Lab/Procedure search
  const onSearch = async (value) => {
    setSearchValue(value);
    setSelectedOrder(null);
    if (value.length > 2) {
      setIsSearching(true);
      try {
        const result = await ConsultationService.LabTestMDM(value);
        setSearchResults(result?.data?.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectResult = (item) => {
    setSelectedOrder({
      orderName: item.testName || item.name || "",
      serviceCode: item.doxzonLabId || item.labCode || item.code || "",
    });
    setSearchValue(item.testName || item.name || "");
    setSearchResults([]);
  };

  const handleClearSearch = () => {
    setSelectedOrder(null);
    setSearchValue("");
    setSearchResults([]);
  };

  // Medication search via DrugServices.fetchElasticDrugListnew (same API as Medicine.jsx)
  const onSearchMedicine = async (value) => {
    setMedSearchValue(value);
    setSelectedMedItem(null);
    if (value && value.trim().length > 1) {
      setIsMedSearching(true);
      const payload = {
        medicineName: value.trim(),
        pageNo: 1,
        size: 40,
        userId: currentUserId || userData?.user?.userId || "",
      };
      if (userData?.user?.userId === "DZU3191271") {
        payload.pharmacyId = "DZA1361124";
      }

      try {
        const result = await DrugServices.fetchElasticDrugListnew(payload);
        setMedSearchResults(result?.data?.data ?? []);
      } catch (err) {
        console.error("Error searching medicine:", err);
        setMedSearchResults([]);
      } finally {
        setIsMedSearching(false);
      }
    } else {
      setMedSearchResults([]);
    }
  };

  const handleSelectMedResult = (med) => {
    setSelectedMedItem(med);
    setMedSearchValue(med.medicineName || "");
    setMedSearchResults([]);
  };

  const handleClearMedSearch = () => {
    setSelectedMedItem(null);
    setMedSearchValue("");
    setMedSearchResults([]);
  };

  const handleAddMedication = () => {
    const orderName = selectedMedItem?.medicineName || medSearchValue.trim();
    if (!orderName) {
      message.error("Please enter or select a medicine");
      return;
    }

    const qty = Number(medQuantity) || 1;
    const newMed = {
      orderName,
      quantity: qty > 0 ? qty : 1,
      instructions: medInstructions.trim(),
      serviceCode: selectedMedItem?.code || "",
    };

    setMedicationsList((prev) => [...prev, newMed]);
    setSelectedMedItem(null);
    setMedSearchValue("");
    setMedQuantity(1);
    setMedInstructions("");
    setMedSearchResults([]);
  };

  const handleRemoveMedication = (index) => {
    setMedicationsList((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    if (!formData.orderType) {
      message.error("Please select Order Type");
      return false;
    }
    if (!formData.orderDate) {
      message.error("Please select Order Date");
      return false;
    }

    if (formData.orderType === "MEDICATION") {
      const pendingName = selectedMedItem?.medicineName || medSearchValue.trim();
      if (medicationsList.length === 0 && !pendingName) {
        message.error("Please add at least one medication");
        return false;
      }
    } else {
      const orderName = selectedOrder?.orderName || searchValue.trim();
      if (!orderName) {
        message.error("Please enter an Order Name / Test");
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    let finalMedList = [...medicationsList];
    if (formData.orderType === "MEDICATION") {
      const pendingName = selectedMedItem?.medicineName || medSearchValue.trim();
      if (pendingName) {
        finalMedList.push({
          orderName: pendingName,
          quantity: Number(medQuantity) > 0 ? Number(medQuantity) : 1,
          instructions: medInstructions.trim(),
          serviceCode: selectedMedItem?.code || "",
        });
      }
    }

    const nowStr = formatDateTimePayload(formData.orderDate);
    const orderName = selectedOrder?.orderName || searchValue.trim();
    const serviceCode = selectedOrder?.serviceCode || "";

    const orderedByName =
      userData?.user?.firstName || userData?.user?.lastName
        ? `${userData?.user?.firstName || ""} ${userData?.user?.lastName || ""}`.trim()
        : userData?.user?.userName || "Doctor";

    const patientIpdOrderDtl =
      formData.orderType === "MEDICATION"
        ? finalMedList.map((item) => ({
            createdBy: currentUserId,
            createdDate: nowStr,
            instructions: item.instructions || formData.clinicalNotes || "",
            modifiedBy: currentUserId,
            modifiedDate: nowStr,
            orderName: item.orderName,
            orderType: "MEDICATION",
            patientId: effectivePatientId,
            quantity: Number(item.quantity) || 1,
            serviceCode: item.serviceCode || "",
            status: 1,
          }))
        : [
            {
              createdBy: currentUserId,
              createdDate: nowStr,
              instructions: formData.instructions || "NA",
              modifiedBy: currentUserId,
              modifiedDate: nowStr,
              orderName: orderName,
              orderType: formData.orderType,
              patientId: effectivePatientId,
              quantity: 1,
              serviceCode: serviceCode,
              status: 1,
            },
          ];

    const payload = {
      accountId: effectiveAccountId || userData?.user?.account_Id || "",
      admissionId: effectiveAdmissionId
        ? isNaN(Number(effectiveAdmissionId))
          ? effectiveAdmissionId
          : Number(effectiveAdmissionId)
        : "",
      clinicId: effectiveClinicId || userData?.user?.clinic_Id || "",
      clinicalNotes: formData.clinicalNotes || "",
      createdBy: currentUserId,
      createdDate: nowStr,
      departmentCode: departmentCode,
      modifiedBy: currentUserId,
      modifiedDate: nowStr,
      orderDate: nowStr,
      orderType: formData.orderType,
      orderedBy: orderedByName,
      patientId: effectivePatientId,
      patientIpdOrderDtl: patientIpdOrderDtl,
      priority: (formData.priority || "MEDIUM").toUpperCase(),
      status: 1,
    };

    console.log("Saving IPD Order Payload:", payload);

    setIsSaving(true);
    try {
      await IPDOrderService.saveIPDOrder(payload);
      message.success("Order saved successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Save order error:", err);
      message.error("Failed to save order. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-[18px] overflow-hidden shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#edf1f7]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#f0fdfd] flex items-center justify-center text-[#008f9f]">
              <FaPills className="text-[15px]" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-[#111827]">Add Order</h2>
              <p className="text-[11px] text-[#6b7280]">
                Create new inpatient clinical or pharmacy order
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center bg-white border border-[#e5e7eb] justify-center rounded-full hover:bg-gray-100 text-[#6b7280] transition-colors"
          >
            <FiX className="text-[15px]" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="p-6 space-y-5 max-h-[72vh] overflow-y-auto">
          {/* Row 1: Order Type & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                Order Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.orderType}
                  onChange={(e) => handleField("orderType", e.target.value)}
                  className="w-full h-10 px-3 pr-8 border border-[#e5e7eb] rounded-lg text-[13px] text-[#374151] outline-none appearance-none focus:border-[#008f9f] bg-white cursor-pointer"
                >
                  <option value="">Select Order Type</option>
                  {ORDER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-3 top-3 text-[#6b7280]" />
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                Priority
              </label>
              <div className="relative">
                <select
                  value={formData.priority}
                  onChange={(e) => handleField("priority", e.target.value)}
                  className="w-full h-10 px-3 pr-8 border border-[#e5e7eb] rounded-lg text-[13px] text-[#374151] outline-none appearance-none focus:border-[#008f9f] bg-white cursor-pointer"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-3 top-3 text-[#6b7280]" />
              </div>
            </div>
          </div>

          {/* Row 2: Order Date (Always shown) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                Order Date <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg h-10 px-3 focus-within:border-[#008f9f] bg-white">
                <FiCalendar className="text-[#6b7280] text-[13px] shrink-0" />
                <input
                  type="date"
                  value={formData.orderDate}
                  onChange={(e) => handleField("orderDate", e.target.value)}
                  className="flex-1 outline-none text-[13px] text-[#374151] bg-transparent"
                />
              </div>
            </div>

            {/* If NOT medication, show instructions here */}
            {formData.orderType !== "MEDICATION" && (
              <div>
                <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                  Instructions
                </label>
                <input
                  type="text"
                  value={formData.instructions}
                  onChange={(e) => handleField("instructions", e.target.value)}
                  placeholder="e.g. Fasting required, contrast dye"
                  className="w-full h-10 px-3 border border-[#e5e7eb] rounded-lg text-[13px] text-[#374151] outline-none focus:border-[#008f9f] placeholder:text-[#9ca3af]"
                />
              </div>
            )}
          </div>

          {/* If MEDICATION is selected: Multi-medicine Search & Add Section */}
          {formData.orderType === "MEDICATION" ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-[12px] font-semibold text-[#374151]">
                  Medications <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-[#6b7280]">
                  Add multiple medicines to this pharmacy order
                </span>
              </div>

              {/* Medicine Input Row */}
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-3.5 space-y-3">
                <div className="grid grid-cols-12 gap-3 items-end">
                  {/* Medicine Name with Search */}
                  <div className="col-span-12 sm:col-span-5 relative" ref={medSearchRef}>
                    <label className="block text-[11px] font-semibold text-[#475569] mb-1">
                      Medicine Name <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2 border border-[#cbd5e1] rounded-lg h-9 px-2.5 focus-within:border-[#008f9f] bg-white">
                      <FiSearch className="text-[#94a3b8] text-[13px] shrink-0" />
                      <input
                        type="text"
                        value={medSearchValue}
                        onChange={(e) => onSearchMedicine(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddMedication();
                          }
                        }}
                        placeholder="Search medicine..."
                        className="flex-1 outline-none text-[12px] text-[#334155] bg-transparent placeholder:text-[#94a3b8]"
                      />
                      {isMedSearching && (
                        <FaSpinner className="animate-spin text-[#008f9f] text-[11px]" />
                      )}
                      {medSearchValue && !isMedSearching && (
                        <button
                          type="button"
                          onClick={handleClearMedSearch}
                          className="text-[#94a3b8] hover:text-[#334155]"
                        >
                          <FiX className="text-[12px]" />
                        </button>
                      )}
                    </div>

                    {/* Autocomplete Dropdown */}
                    {medSearchResults.length > 0 && (
                      <ul className="absolute left-0 top-[60px] w-full bg-white border border-[#cbd5e1] rounded-lg shadow-xl max-h-48 overflow-y-auto z-30">
                        {medSearchResults.map((med, idx) => (
                          <li
                            key={med.id || med.code || idx}
                            onClick={() => handleSelectMedResult(med)}
                            className="px-3 py-2 text-[12px] text-[#334155] hover:bg-[#f0fdfa] hover:text-[#008f9f] cursor-pointer border-b border-[#f1f5f9] last:border-0"
                          >
                            <div className="font-medium text-[#1e293b]">
                              {med.medicineName}
                            </div>
                            {med.saltComposition && (
                              <div className="text-[10px] text-[#64748b] truncate mt-0.5">
                                {med.saltComposition}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Quantity */}
                  <div className="col-span-4 sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-[#475569] mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={medQuantity}
                      onChange={(e) => setMedQuantity(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddMedication();
                        }
                      }}
                      placeholder="Qty"
                      className="w-full h-9 px-2.5 border border-[#cbd5e1] rounded-lg text-[12px] text-[#334155] outline-none focus:border-[#008f9f] bg-white"
                    />
                  </div>

                  {/* Instructions */}
                  <div className="col-span-8 sm:col-span-4">
                    <label className="block text-[11px] font-semibold text-[#475569] mb-1">
                      Instructions
                    </label>
                    <input
                      type="text"
                      value={medInstructions}
                      onChange={(e) => setMedInstructions(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddMedication();
                        }
                      }}
                      placeholder="e.g. 1 tab after food"
                      className="w-full h-9 px-2.5 border border-[#cbd5e1] rounded-lg text-[12px] text-[#334155] outline-none focus:border-[#008f9f] bg-white placeholder:text-[#94a3b8]"
                    />
                  </div>

                  {/* Add Button */}
                  <div className="col-span-12 sm:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddMedication}
                      title="Add medicine to list"
                      className="w-full h-9 bg-[#008f9f] text-white rounded-lg hover:bg-[#007a8a] transition-colors flex items-center justify-center gap-1 text-[12px] font-semibold shadow-sm"
                    >
                      <FiPlus className="text-[14px]" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Added Medications Table */}
              {medicationsList.length > 0 ? (
                <div className="border border-[#e2e8f0] rounded-xl overflow-hidden bg-white shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                      <tr>
                        <th className="py-2.5 px-3 text-[11px] font-semibold text-[#64748b] w-10">
                          #
                        </th>
                        <th className="py-2.5 px-3 text-[11px] font-semibold text-[#64748b]">
                          Medicine Name (orderName)
                        </th>
                        <th className="py-2.5 px-3 text-[11px] font-semibold text-[#64748b] w-20 text-center">
                          Quantity
                        </th>
                        <th className="py-2.5 px-3 text-[11px] font-semibold text-[#64748b]">
                          Instructions
                        </th>
                        <th className="py-2.5 px-3 text-[11px] font-semibold text-[#64748b] w-14 text-center">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f1f5f9]">
                      {medicationsList.map((item, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-[#fafbff] transition-colors"
                        >
                          <td className="py-2 px-3 text-[12px] text-[#94a3b8]">
                            {idx + 1}
                          </td>
                          <td className="py-2 px-3 text-[12px] font-medium text-[#1e293b]">
                            {item.orderName}
                          </td>
                          <td className="py-2 px-3 text-[12px] text-[#334155] text-center">
                            <span className="inline-block bg-teal-50 text-teal-700 font-semibold px-2 py-0.5 rounded text-[11px]">
                              {item.quantity}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-[12px] text-[#64748b]">
                            {item.instructions || (
                              <span className="text-[#94a3b8] italic">
                                None
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveMedication(idx)}
                              className="w-7 h-7 inline-flex items-center justify-center text-red-500 hover:bg-red-50 rounded-md transition-colors"
                              title="Remove"
                            >
                              <FiTrash2 className="text-[13px]" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-5 border border-dashed border-[#cbd5e1] rounded-xl text-center text-[#94a3b8] text-[12px] bg-[#fafafa]">
                  No medicines added yet. Enter medicine details above and click + to add.
                </div>
              )}
            </div>
          ) : (
            /* Non-Medication Order Name / Test search */
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
                Order Name / Test <span className="text-red-500">*</span>
              </label>
              <div ref={searchRef}>
                {/* Search input */}
                <div className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg h-10 px-3 focus-within:border-[#008f9f] bg-white">
                  <FiSearch className="text-[#9ca3af] text-[13px] shrink-0" />
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Search test / procedure..."
                    className="flex-1 outline-none text-[13px] text-[#374151] bg-transparent placeholder:text-[#9ca3af]"
                  />
                  {isSearching && (
                    <FaSpinner className="animate-spin text-[#008f9f] text-[12px]" />
                  )}
                  {searchValue && !isSearching && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="text-[#9ca3af] hover:text-[#374151]"
                    >
                      <FiX className="text-[12px]" />
                    </button>
                  )}
                </div>

                {/* Inline results list */}
                {searchResults.length > 0 && (
                  <ul className="mt-1 bg-white border border-[#e5e7eb] rounded-lg max-h-40 overflow-y-auto">
                    {searchResults.map((item, idx) => (
                      <li
                        key={item.id || idx}
                        onClick={() => handleSelectResult(item)}
                        className="px-3 py-2 text-[13px] text-[#374151] hover:bg-[#f0fdfd] hover:text-[#008f9f] cursor-pointer border-b border-[#f3f4f6] last:border-0"
                      >
                        <span className="font-medium">
                          {item.testName || item.name}
                        </span>
                        {item.doxzonLabId && (
                          <span className="ml-2 text-[11px] text-[#9ca3af]">
                            ({item.doxzonLabId})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Clinical Notes / Indication */}
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">
              {formData.orderType === "MEDICATION"
                ? "Pharmacy Order Notes / Clinical Indication"
                : "Clinical Notes / Indication"}
            </label>
            <div className="relative border border-[#e5e7eb] rounded-lg overflow-hidden focus-within:border-[#008f9f]">
              <textarea
                rows={3}
                maxLength={MAX_CHARS}
                value={formData.clinicalNotes}
                onChange={(e) => handleField("clinicalNotes", e.target.value)}
                placeholder={
                  formData.orderType === "MEDICATION"
                    ? "Enter pharmacy order notes (e.g. Pharmacy Order Notes)"
                    : "Enter clinical indication or notes (optional)"
                }
                className="w-full p-3 text-[13px] text-[#374151] outline-none resize-none bg-white"
              />
              <div className="px-3 pb-2 text-right text-[11px] text-[#9ca3af]">
                {formData.clinicalNotes.length}/{MAX_CHARS}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-[#edf1f7] flex justify-end gap-3 bg-[#fafbff]">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-2 bg-white border border-[#e5e7eb] rounded-lg text-[13px] font-semibold text-[#374151] hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 rounded-lg  bg-[#008f9f] text-white text-[13px] font-semibold hover:bg-[#007a8a] transition-colors flex items-center gap-2 disabled:opacity-60 shadow-sm"
          >
            {isSaving && <FaSpinner className="animate-spin text-[13px]" />}
            {isSaving ? "Saving\u2026" : "Save Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

